/**
 * Shared chrome for hybrid public demos (CLAW Form→Flow→App goldens).
 * Mirrors CitizenAdvisorShell patterns: phases + outcome tray + Studio runtime.
 */
import React, { useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Link, Stack, Step, StepButton, Stepper, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StudioV2AppRuntimeShell from '../studio/StudioV2AppRuntimeShell';
import CitizenOutcomeTray from '../citizen/CitizenOutcomeTray';
import { useDashboard } from '../../contexts/DashboardContext';
import { normalizeInterviewPhases } from '../../utils/interviewFlowRuntime';
import DemoSiteNav from './DemoSiteNav';
import {
  IF_BRAND,
  ensureInterviewFlowFonts,
  interviewFlowCssVars,
} from '../../theme/interviewFlowBrand';

function DemoChrome({
  title, subtitle, helpPath, portalLinks = [], primaryColor,
}) {
  const theme = useTheme();
  const isCompact = useMediaQuery('(max-height:820px), (max-width:1100px)');
  const isSff = useMediaQuery(theme.breakpoints.down('md'));
  const {
    config, interviewPhaseId, setInterviewPhaseId, structuredOutput,
  } = useDashboard();
  const phases = useMemo(
    () => normalizeInterviewPhases(config?.metadata?.interviewFlow?.phases),
    [config?.metadata?.interviewFlow?.phases],
  );
  const activeId = interviewPhaseId
    || config?.metadata?.interviewFlow?.initialPhaseId
    || phases[0]?.id;
  const activeStep = Math.max(0, phases.findIndex((p) => p.id === activeId));
  const hasResult = Boolean(structuredOutput?.routing?.primaryTrack);

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: 2,
      }}
      data-testid="demo-assistant-chrome"
    >
      <Box sx={{
        px: { xs: 1.5, sm: 2 },
        pt: isCompact ? 1.25 : 1.75,
        pb: isCompact ? 1 : 1.25,
        background: IF_BRAND.headerGradient,
      }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" useFlexGap flexWrap="wrap">
          <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: IF_BRAND.display,
                fontWeight: 700,
                fontSize: isCompact ? '1.2rem' : { xs: '1.35rem', sm: '1.55rem' },
                letterSpacing: '-0.03em',
                color: primaryColor,
                lineHeight: 1.15,
              }}
            >
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
              {subtitle || 'Guidance demo'}
              {helpPath ? (
                <>
                  {' · '}
                  <Link component={RouterLink} to={helpPath} underline="hover" color="inherit">
                    How this works
                  </Link>
                </>
              ) : null}
              {isSff && hasResult ? ' · Open path panel from results actions' : null}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
            <DemoSiteNav />
            {portalLinks.map((p) => (
              <Button
                key={p.to}
                component={RouterLink}
                to={p.to}
                size="small"
                variant={p.active ? 'contained' : 'outlined'}
                sx={{
                  textTransform: 'none',
                  minHeight: 30,
                  ...(p.active ? { bgcolor: primaryColor, borderColor: primaryColor } : {}),
                }}
              >
                {p.label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Box>
      {phases.length > 0 && (
        <Box sx={{ px: { xs: 0.5, sm: 1.5 }, py: isCompact ? 0.75 : 1 }}>
          <Stepper activeStep={activeStep} nonLinear>
            {phases.map((ph) => (
              <Step key={ph.id} completed={phases.findIndex((p) => p.id === ph.id) < activeStep}>
                <StepButton onClick={() => setInterviewPhaseId(ph.id)} sx={{ textTransform: 'none', py: 0.25 }}>
                  {String(ph.label || ph.id).replace(/^\d+\s*·\s*/, '')}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
    </Box>
  );
}

export default function DemoAssistantShell({
  definition,
  title,
  subtitle = 'Interactive demo — guidance only, not an official determination',
  helpPath = null,
  portalLinks = [],
  primaryColor = IF_BRAND.accent,
  darkMode = false,
  showOutcomeTray = true,
}) {
  const theme = useTheme();
  const isSff = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => { ensureInterviewFlowFonts(); }, []);

  const citizenDefinition = useMemo(() => {
    if (!definition) return null;
    const iflow = definition.metadata?.interviewFlow || {};
    return {
      ...definition,
      name: title || definition.name,
      theme: {
        ...(definition.theme || {}),
        primaryColor,
        headerStyle: 'flat',
      },
      metadata: {
        ...(definition.metadata || {}),
        interviewFlow: {
          ...iflow,
          citizenMode: true,
          publicAdvise: true,
          helpPath: helpPath || iflow.helpPath,
        },
      },
    };
  }, [definition, title, primaryColor, helpPath]);

  if (!citizenDefinition) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        bgcolor: IF_BRAND.canvas,
        fontFamily: IF_BRAND.body,
        ...interviewFlowCssVars(primaryColor),
      }}
      data-testid="demo-assistant-shell"
    >
      <StudioV2AppRuntimeShell
        definition={citizenDefinition}
        mongoId={citizenDefinition.id || citizenDefinition.name}
        studioPreview
        demoReadOnly
        citizenMode
        hideAppHeader
        darkMode={darkMode}
        height="100%"
        chrome={(
          <DemoChrome
            title={title || citizenDefinition.name}
            subtitle={subtitle}
            helpPath={helpPath}
            portalLinks={portalLinks}
            primaryColor={primaryColor}
          />
        )}
        aside={showOutcomeTray && !isSff ? <CitizenOutcomeTray /> : null}
      />
    </Box>
  );
}
